import { supabase, CampanhaCupom, CupomCliente, SorteioCupom, GanhadorSorteio } from '@/lib/supabase';
import { httpInterceptor } from './http.interceptor';
import { getSubdomainFranchise } from '@/utils/get-subdomain';


class CouponsService {
  private subdomain: string;
  
  constructor() {
    this.subdomain = getSubdomainFranchise();
  }
  // Buscar campanha ativa
  async getCampanhaAtiva(): Promise<CampanhaCupom | null> {
    try {
      const agora = new Date().toISOString();
      
      const { data, error } = await supabase
        .from('campanhas_cupons')
        .select('*')
        .eq('status', 'ativa')
        .lte('periodo_inicio', agora)
        .gte('periodo_fim', agora)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // Nenhuma campanha encontrada
          return null;
        }
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Erro ao buscar campanha ativa:', error);
      return null;
    }
  }

  // Buscar cupons do cliente
  async getCuponsCliente(clienteId: number): Promise<CupomCliente[]> {
    try {
      const { data, error } = await supabase
        .from('cupons_clientes')
        .select('*')
        .eq('cliente_id', clienteId)
        .order('data_geracao', { ascending: false });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Erro ao buscar cupons do cliente:', error);
      return [];
    }
  }

  // Buscar próximos sorteios da campanha ativa
  async getProximosSorteios(): Promise<SorteioCupom[]> {
    try {
      const campanha = await this.getCampanhaAtiva();
      if (!campanha) return [];

      const agora = new Date().toISOString();

      const { data, error } = await supabase
        .from('sorteios_cupons')
        .select('*')
        .eq('campanha_id', campanha.id)
        .eq('status', 'pendente')
        .gte('data_sorteio', agora)
        .order('data_sorteio', { ascending: true });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Erro ao buscar próximos sorteios:', error);
      return [];
    }
  }

  // Buscar todas campanhas ativas no periodo atual
  async getCampanhasAtivas(): Promise<CampanhaCupom[]> {
    try {
      const agora = new Date().toISOString();

      const { data, error } = await supabase
        .from('campanhas_cupons')
        .select('*')
        .eq('status', 'ativa')
        .lte('periodo_inicio', agora)
        .gte('periodo_fim', agora)
        .order('periodo_inicio', { ascending: true });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Erro ao buscar campanhas ativas:', error);
      return [];
    }
  }

  // Buscar ganhadores anteriores (últimos 20)
  async getGanhadoresAnteriores(): Promise<GanhadorSorteio[]> {
    try {
      const { data, error } = await supabase
        .from('ganhadores_sorteios')
        .select(`
          *,
          sorteio:sorteios_cupons(titulo, data_sorteio)
        `)
        .eq('validado', true)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Erro ao buscar ganhadores anteriores:', error);
      return [];
    }
  }

  // Calcular dias restantes de validade
  getDiasRestantes(dataValidade: string): number {
    const hoje = new Date();
    const validade = new Date(dataValidade);
    const diffTime = validade.getTime() - hoje.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  // Verificar se cupom está próximo do vencimento (menos de 7 dias)
  isProximoVencimento(dataValidade: string): boolean {
    return this.getDiasRestantes(dataValidade) <= 7 && this.getDiasRestantes(dataValidade) > 0;
  }

  // Admin: Exportar dados de cupons para CSV
  async exportCuponsCSV(clienteId?: number): Promise<string> {
    try {
      let query = supabase
        .from('cupons_clientes')
        .select('*, usuarios(nome, codigo, email)');

      if (clienteId) {
        query = query.eq('cliente_id', clienteId);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Gerar CSV
      const headers = ['ID', 'Cliente', 'Número Cupom', 'Quantidade', 'Status', 'Origem', 'Data Geração', 'Data Validade', 'Valor Compra'];
      const rows = data?.map((cupom: any) => [
        cupom.id,
        cupom.usuarios?.nome || 'N/A',
        cupom.numero_cupom,
        cupom.quantidade_cupons,
        cupom.status,
        cupom.origem,
        cupom.data_geracao,
        cupom.data_validade,
        cupom.valor_compra || 'N/A',
      ]) || [];

      const csv = [headers, ...rows].map((row) => row.join(',')).join('\n');
      return csv;
    } catch (error) {
      console.error('Erro ao exportar cupons:', error);
      throw error;
    }
  }

  // Admin: Estatísticas gerais de cupons
  async getEstatisticasGerais() {
    try {
      const [cupons, sorteios, ganhadores] = await Promise.all([
        supabase.from('cupons_clientes').select('status, quantidade_cupons'),
        supabase.from('sorteios_cupons').select('status'),
        supabase.from('ganhadores_sorteios').select('validado'),
      ]);

      const totalAtivos = cupons.data?.reduce((sum, c: any) => 
        c.status === 'ativo' ? sum + c.quantidade_cupons : sum, 0) || 0;
      const totalInativos = cupons.data?.reduce((sum, c: any) => 
        c.status === 'inativo' ? sum + c.quantidade_cupons : sum, 0) || 0;
      const totalUsados = cupons.data?.reduce((sum, c: any) => 
        c.status === 'usado' ? sum + c.quantidade_cupons : sum, 0) || 0;

      const totalSorteios = sorteios.data?.length || 0;
      const sorteiosRealizados = sorteios.data?.filter((s: any) => s.status === 'realizado').length || 0;
  const sorteioPendentes = sorteios.data?.filter((s: any) => s.status === 'pendente').length || 0;

      const ganhadoresValidados = ganhadores.data?.filter((g: any) => g.validado).length || 0;

      return {
        cupons: {
          totalAtivos,
          totalInativos,
          totalUsados,
          total: totalAtivos + totalInativos + totalUsados,
        },
        sorteios: {
          total: totalSorteios,
          realizados: sorteiosRealizados,
          pendentes: sorteioPendentes,
        },
        ganhadores: {
          total: ganhadores.data?.length || 0,
          validados: ganhadoresValidados,
        },
      };
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
      return null;
    }
  }

  // Admin: Validar sorteio realizado
  async validarSorteioRealizado(sorteioId: string, dataRealizacao: string) {
    try {
      const { error } = await supabase
        .from('sorteios_cupons')
        .update({
          status: 'realizado',
          realizado_em: dataRealizacao,
          updated_at: new Date().toISOString(),
        })
        .eq('id', sorteioId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Erro ao validar sorteio:', error);
      throw error;
    }
  }

  // Admin: Registrar ganhador sorteio
  async registrarGanhadorSorteio(sorteioId: string, cupomId: string, clienteId: number) {
    try {
      // Buscar informações necessárias
      const [cupom, cliente, sorteio] = await Promise.all([
        supabase.from('cupons_clientes').select('*').eq('id', cupomId).single(),
        supabase.from('usuarios').select('*').eq('id', clienteId).single(),
        supabase.from('sorteios_cupons').select('*').eq('id', sorteioId).single(),
      ]);

      const { data: ganhador, error } = await supabase
        .from('ganhadores_sorteios')
        .insert([
          {
            sorteio_id: sorteioId,
            cupom_id: cupomId,
            cliente_id: clienteId,
            cliente_nome: cliente.data?.nome,
            cliente_codigo: cliente.data?.codigo,
            cliente_instagram: cliente.data?.instagram,
            revendedora_id: cupom.data?.revendedora_venda,
            premio_descricao: sorteio.data?.premio_descricao,
            validado: false,
          },
        ])
        .select();

      if (error) throw error;
      return ganhador;
    } catch (error) {
      console.error('Erro ao registrar ganhador:', error);
      throw error;
    }
  }

  // Admin: Carregar usuários com cupons do endpoint
  async getUsuariosComCupons() {
    try {
      const response = await httpInterceptor.get('/coupons/users');
      
      if (!response.ok) {
        throw new Error(`Erro ao carregar usuários: ${response.statusText}`);
      }

      const data = await response.json();

      // Transformar dados do endpoint para o formato esperado
      const usuariosData = data.map((user: any) => ({
        cliente_id: user.id,
        cliente_nome: user.nome,
        cliente_codigo: user.codigo,
        cliente_email: user.email,
        cliente_instagram: user.instagram,
        cliente_telefone: user.telefone,
        cliente_cidade: user.cidade,
        cliente_estado: user.estado,
        total_cupons: user.quantidade_cupons || 0,
        total_ativos: user.quantidade_cupons || 0,
        total_inativos: 0,
        cupons: [],
      }));

      return usuariosData;
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      throw error;
    }
  }

  // Admin: Adicionar novo ganhador
  async adicionarGanhador(sorteioId: string, clienteData: any) {
    try {
      // Buscar um cupom do cliente
      const { data: cupomData, error: cupomError } = await supabase
        .from('cupons_clientes')
        .select('id')
        .eq('cliente_id', clienteData.id)
        .limit(1)
        .single();

      if (cupomError || !cupomData) {
        throw new Error('Cliente não possui cupons');
      }

      // Registrar o ganhador
      const { error } = await supabase
        .from('ganhadores_sorteios')
        .insert([
          {
            sorteio_id: sorteioId,
            cupom_id: cupomData.id,
            cliente_id: clienteData.id,
            cliente_nome: clienteData.nome_razao_social,
            cliente_codigo: clienteData.codigo,
            cliente_instagram: clienteData.instagram,
            cliente_cidade: clienteData.cidade,
            cliente_estado: clienteData.estado,
            validado: false,
          },
        ]);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Erro ao adicionar ganhador:', error);
      throw error;
    }
  }

  // Admin: Buscar cupons de um cliente
  async getClientCoupons(clienteId: number): Promise<any[]> {
    try {
      const response = await httpInterceptor.get(`/coupons/client/${clienteId}`);

      if (!response.ok) {
        throw new Error(`Erro ao buscar cupons do cliente: ${response.statusText}`);
      }

      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error('Erro ao buscar cupons do cliente:', error);
      throw error;
    }
  }

  // Admin: Atualizar status do cupom
  async updateCupomStatus(
    cupomId: string,
    novoStatus: 'ativo' | 'inativo' | 'cancelado',
    motivoCancelamento?: string
  ): Promise<void> {
    try {
      const updatePayload: any = {
        status: novoStatus,
        updated_at: new Date().toISOString(),
      };

      // Se o status é cancelado, adicionar motivo e data de cancelamento
      if (novoStatus === 'cancelado') {
        updatePayload.cancelado_em = new Date().toISOString();
        if (motivoCancelamento) {
          updatePayload.motivo_cancelamento = motivoCancelamento;
        }
      }

      const { error } = await supabase
        .from('cupons_clientes')
        .update(updatePayload)
        .eq('id', cupomId);

      if (error) throw error;
    } catch (error) {
      console.error('Erro ao atualizar status do cupom:', error);
      throw error;
    }
  }

  // Admin: Exportar cupons ativos como CSV
  async exportActiveCouponsCSV(): Promise<Blob> {
    try {
      const response = await httpInterceptor.get('/coupons/export/active');

      if (!response.ok) {
        throw new Error(`Erro ao exportar cupons: ${response.statusText}`);
      }

      const blob = await response.blob();
      return blob;
    } catch (error) {
      console.error('Erro ao exportar cupons ativos:', error);
      throw error;
    }
  }
}

export const couponsService = new CouponsService();
